'use client'

import * as React from 'react'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { XIcon } from 'lucide-react'
import { cn } from '../../helpers/classnames'
import { JosefIconButton } from '../buttons/josef-icon-button'

const JosefSheetContext = React.createContext<{ modal: boolean }>({
  modal: true,
})

interface JosefSheetProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root> {
  modal?: boolean
}

const JosefSheet = ({ modal = true, ...props }: JosefSheetProps) => (
  <JosefSheetContext.Provider value={{ modal }}>
    <SheetPrimitive.Root modal={modal} {...props} />
  </JosefSheetContext.Provider>
)

const JosefSheetTrigger = SheetPrimitive.Trigger

const JosefSheetClose = SheetPrimitive.Close

const JosefSheetPortal = SheetPrimitive.Portal

const JosefSheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    className={cn(
      'josef-sheet-overlay fixed inset-0 z-40 bg-black/50',
      className
    )}
    style={{ pointerEvents: 'auto' }}
    {...props}
  />
))
JosefSheetOverlay.displayName = 'JosefSheetOverlay'

type SheetSide = 'top' | 'bottom' | 'left' | 'right'
type SheetSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full'

const sideBaseStyles: Record<SheetSide, string> = {
  top: 'inset-x-2 top-2 rounded-lg',
  bottom: 'inset-x-2 bottom-2 rounded-lg',
  left: 'top-2 bottom-2 left-2 w-3/4 rounded-lg',
  right: 'top-2 bottom-2 right-0 w-3/4 rounded-l-lg',
}

const sideBaseStylesFullMobile: Record<SheetSide, string> = {
  top: 'inset-x-0 top-0 sm:inset-x-2 sm:top-2 rounded-none sm:rounded-lg',
  bottom:
    'inset-x-0 bottom-0 sm:inset-x-2 sm:bottom-2 rounded-none sm:rounded-lg',
  left: 'inset-y-0 left-0 w-full sm:top-2 sm:bottom-2 sm:left-2 sm:w-3/4 rounded-none sm:rounded-lg',
  right:
    'inset-y-0 right-0 w-full sm:top-2 sm:bottom-2 sm:w-3/4 rounded-none sm:rounded-l-lg',
}

const sizeStyles: Record<SheetSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  full: 'sm:max-w-full',
}

interface JosefSheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
  side?: SheetSide
  size?: SheetSize
  fullMobile?: boolean
  hideCloseButton?: boolean
  preventAutoFocus?: boolean
  preventOutsideClose?: boolean
  onOverlayClick?: () => void
}

const JosefSheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  JosefSheetContentProps
>(
  (
    {
      side = 'right',
      size = 'sm',
      // Default: sheets are full-width on mobile, sized on desktop.
      fullMobile = true,
      className,
      children,
      hideCloseButton = false,
      preventAutoFocus = true,
      preventOutsideClose = true,
      onOpenAutoFocus,
      onInteractOutside,
      onOverlayClick,
      ...props
    },
    ref
  ) => {
    const { modal } = React.useContext(JosefSheetContext)

    return (
      <JosefSheetPortal>
        {modal ? (
          <JosefSheetOverlay
            onClick={
              preventOutsideClose ? (e) => e.stopPropagation() : onOverlayClick
            }
          />
        ) : (
          <div
            className="josef-sheet-overlay fixed inset-0 z-40 bg-black/50"
            onClick={preventOutsideClose ? undefined : onOverlayClick}
            style={{ pointerEvents: 'auto' }}
          />
        )}
        <SheetPrimitive.Content
          ref={ref}
          className={cn(
            'josef-sheet-content fixed z-40 flex flex-col gap-4 bg-neutral-50 dark:bg-neutral-900 shadow-lg overflow-y-auto overflow-x-hidden',
            fullMobile ? sideBaseStylesFullMobile[side] : sideBaseStyles[side],
            (side === 'left' || side === 'right') && sizeStyles[size],
            className
          )}
          data-side={side}
          onOpenAutoFocus={
            preventAutoFocus ? (e) => e.preventDefault() : onOpenAutoFocus
          }
          onInteractOutside={(e) => {
            if (preventOutsideClose) {
              e.preventDefault()
            }
            onInteractOutside?.(e)
          }}
          onPointerDownOutside={(e) => {
            if (preventOutsideClose) {
              e.preventDefault()
            }
          }}
          {...props}
        >
          <style
            dangerouslySetInnerHTML={{
              __html: `
          @keyframes josef-sheet-overlay-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes josef-sheet-overlay-out {
            from { opacity: 1; }
            to { opacity: 0; }
          }
          @keyframes josef-sheet-slide-in-from-right {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          @keyframes josef-sheet-slide-out-to-right {
            from { transform: translateX(0); }
            to { transform: translateX(100%); }
          }
          @keyframes josef-sheet-slide-in-from-left {
            from { transform: translateX(-100%); }
            to { transform: translateX(0); }
          }
          @keyframes josef-sheet-slide-out-to-left {
            from { transform: translateX(0); }
            to { transform: translateX(-100%); }
          }
          @keyframes josef-sheet-slide-in-from-top {
            from { transform: translateY(-100%); }
            to { transform: translateY(0); }
          }
          @keyframes josef-sheet-slide-out-to-top {
            from { transform: translateY(0); }
            to { transform: translateY(-100%); }
          }
          @keyframes josef-sheet-slide-in-from-bottom {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          @keyframes josef-sheet-slide-out-to-bottom {
            from { transform: translateY(0); }
            to { transform: translateY(100%); }
          }
          .josef-sheet-overlay[data-state="open"] {
            animation: josef-sheet-overlay-in 300ms ease-out;
          }
          .josef-sheet-overlay[data-state="closed"] {
            animation: josef-sheet-overlay-out 300ms ease-in;
          }
          .josef-sheet-content[data-side="right"][data-state="open"] {
            animation: josef-sheet-slide-in-from-right 300ms cubic-bezier(0.32, 0.72, 0, 1);
          }
          .josef-sheet-content[data-side="right"][data-state="closed"] {
            animation: josef-sheet-slide-out-to-right 300ms cubic-bezier(0.32, 0.72, 0, 1);
          }
          .josef-sheet-content[data-side="left"][data-state="open"] {
            animation: josef-sheet-slide-in-from-left 300ms cubic-bezier(0.32, 0.72, 0, 1);
          }
          .josef-sheet-content[data-side="left"][data-state="closed"] {
            animation: josef-sheet-slide-out-to-left 300ms cubic-bezier(0.32, 0.72, 0, 1);
          }
          .josef-sheet-content[data-side="top"][data-state="open"] {
            animation: josef-sheet-slide-in-from-top 300ms cubic-bezier(0.32, 0.72, 0, 1);
          }
          .josef-sheet-content[data-side="top"][data-state="closed"] {
            animation: josef-sheet-slide-out-to-top 300ms cubic-bezier(0.32, 0.72, 0, 1);
          }
          .josef-sheet-content[data-side="bottom"][data-state="open"] {
            animation: josef-sheet-slide-in-from-bottom 300ms cubic-bezier(0.32, 0.72, 0, 1);
          }
          .josef-sheet-content[data-side="bottom"][data-state="closed"] {
            animation: josef-sheet-slide-out-to-bottom 300ms cubic-bezier(0.32, 0.72, 0, 1);
          }
        `,
            }}
          />
          {children}
          {!hideCloseButton && (
            <SheetPrimitive.Close asChild>
              <JosefIconButton
                icon={<XIcon className="h-4 w-4" />}
                aria-label="Close"
                variant="ghost"
                size="icon-sm"
                className="absolute right-4 top-4"
              />
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Content>
      </JosefSheetPortal>
    )
  }
)
JosefSheetContent.displayName = 'JosefSheetContent'

const JosefSheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col gap-1.5 p-4 border-b border-black/5 dark:border-white/5',
      className
    )}
    {...props}
  />
)
JosefSheetHeader.displayName = 'JosefSheetHeader'

const JosefSheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'mt-auto flex flex-col gap-2 p-4 border-t border-black/5 dark:border-white/5',
      className
    )}
    {...props}
  />
)
JosefSheetFooter.displayName = 'JosefSheetFooter'

const JosefSheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold text-black dark:text-white',
      className
    )}
    {...props}
  />
))
JosefSheetTitle.displayName = 'JosefSheetTitle'

const JosefSheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-sm text-black/60 dark:text-white/60', className)}
    {...props}
  />
))
JosefSheetDescription.displayName = 'JosefSheetDescription'

export {
  JosefSheet,
  JosefSheetPortal,
  JosefSheetOverlay,
  JosefSheetTrigger,
  JosefSheetClose,
  JosefSheetContent,
  JosefSheetHeader,
  JosefSheetFooter,
  JosefSheetTitle,
  JosefSheetDescription,
}
